import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { EmployeeList } from '../components/maintenance/EmployeeList';
import { EmployeeForm } from '../components/maintenance/EmployeeForm';
import { ConfirmationModal } from '../components/shared/ConfirmationModal';
import {
  MaintenanceEmployee,
  CreateMaintenanceEmployeeInput,
  UpdateMaintenanceEmployeeInput,
  MaintenanceHistoryEntry,
} from '../types/maintenance';

export const EmployeeManagementPage: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<MaintenanceEmployee | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyEmployee, setHistoryEmployee] = useState<MaintenanceEmployee | null>(null);
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: '',
  });
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'deactivate' | 'delete' | 'deleteConfirm' | null;
    employee: MaintenanceEmployee | null;
  }>({
    isOpen: false,
    type: null,
    employee: null,
  });

  const queryClient = useQueryClient();

  // Fetch all employees
  const { data: employees, isLoading: employeesLoading, error } = useQuery({
    queryKey: ['maintenance-employees'],
    queryFn: async () => {
      const response = await api.get('/maintenance-employees');
      return response.data as MaintenanceEmployee[];
    },
    retry: 1,
  });

  // Fetch employee maintenance history
  const { data: maintenanceHistory, isLoading: historyLoading } = useQuery({
    queryKey: ['employee-maintenance-history', historyEmployee?.id, dateFilter],
    queryFn: async () => {
      if (!historyEmployee) return [];

      const params = new URLSearchParams();
      if (dateFilter.startDate) params.append('startDate', dateFilter.startDate);
      if (dateFilter.endDate) params.append('endDate', dateFilter.endDate);

      const response = await api.get(
        `/maintenance-employees/${historyEmployee.id}/history?${params}`
      );
      return response.data as MaintenanceHistoryEntry[];
    },
    enabled: !!historyEmployee && showHistory,
  });

  // Create employee mutation
  const createEmployeeMutation = useMutation({
    mutationFn: async (data: CreateMaintenanceEmployeeInput) => {
      const response = await api.post('/maintenance-employees', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-employees'] });
      setShowForm(false);
      setSelectedEmployee(null);
    },
  });

  // Update employee mutation
  const updateEmployeeMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateMaintenanceEmployeeInput;
    }) => {
      const response = await api.put(`/maintenance-employees/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-employees'] });
      setShowForm(false);
      setSelectedEmployee(null);
    },
  });

  // Deactivate employee mutation
  const deactivateEmployeeMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/maintenance-employees/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-employees'] });
    },
  });

  // Permanently delete employee mutation
  const deleteEmployeeMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/maintenance-employees/${id}/permanent`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-employees'] });
    },
  });

  const handleCreateEmployee = () => {
    setSelectedEmployee(null);
    setShowForm(true);
  };

  const handleEditEmployee = (employee: MaintenanceEmployee) => {
    setSelectedEmployee(employee);
    setShowForm(true);
  };

  const handleDeactivateEmployee = async (employee: MaintenanceEmployee) => {
    setConfirmModal({
      isOpen: true,
      type: 'deactivate',
      employee,
    });
  };

  const handleDeleteEmployee = async (employee: MaintenanceEmployee) => {
    setConfirmModal({
      isOpen: true,
      type: 'delete',
      employee,
    });
  };

  const executeDeactivate = async () => {
    if (!confirmModal.employee) return;
    
    try {
      await deactivateEmployeeMutation.mutateAsync(confirmModal.employee.id);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao desativar funcionário');
    }
  };

  const executeDelete = async () => {
    if (!confirmModal.employee) return;
    
    try {
      await deleteEmployeeMutation.mutateAsync(confirmModal.employee.id);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao excluir funcionário');
    }
  };

  const handleConfirmAction = () => {
    if (confirmModal.type === 'deactivate') {
      executeDeactivate();
      setConfirmModal({ isOpen: false, type: null, employee: null });
    } else if (confirmModal.type === 'delete') {
      // Show second confirmation for delete (don't close modal)
      setConfirmModal({
        isOpen: true,
        type: 'deleteConfirm',
        employee: confirmModal.employee,
      });
    } else if (confirmModal.type === 'deleteConfirm') {
      executeDelete();
      setConfirmModal({ isOpen: false, type: null, employee: null });
    }
  };

  const handleViewHistory = (employee: MaintenanceEmployee) => {
    setHistoryEmployee(employee);
    setShowHistory(true);
  };

  const handleFormSubmit = async (
    data: CreateMaintenanceEmployeeInput | UpdateMaintenanceEmployeeInput
  ) => {
    try {
      if (selectedEmployee) {
        await updateEmployeeMutation.mutateAsync({
          id: selectedEmployee.id,
          data,
        });
      } else {
        await createEmployeeMutation.mutateAsync(data as CreateMaintenanceEmployeeInput);
      }
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || 'Erro ao salvar funcionário'
      );
    }
  };

  const handleExportHistory = () => {
    if (!historyEmployee || !maintenanceHistory) return;

    // Generate CSV content
    const headers = [
      'Máquina',
      'Localização',
      'Início',
      'Fim',
      'Duração (min)',
      'Notas',
    ];
    const rows = maintenanceHistory.map((entry) => [
      entry.machine.code,
      entry.machine.location,
      formatDateTime(entry.session.startTime),
      entry.session.endTime ? formatDateTime(entry.session.endTime) : 'Em andamento',
      entry.session.duration || '-',
      entry.session.notes || '-',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `historico_manutencao_${historyEmployee.name.replace(/\s+/g, '_')}_${
        new Date().toISOString().split('T')[0]
      }.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">Erro ao carregar funcionários</p>
          <p className="text-sm text-gray-600 mb-4">
            {error instanceof Error ? error.message : 'Erro desconhecido'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Funcionários de Manutenção
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Gerencie os funcionários responsáveis pela manutenção das máquinas
          </p>
        </div>
        <button
          onClick={handleCreateEmployee}
          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-500 text-white font-bold rounded-lg hover:from-orange-700 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          Novo Funcionário
        </button>
      </div>

      {/* Employee List */}
      <div className="bg-white shadow rounded-lg p-6">
        <EmployeeList
          employees={employees || []}
          onEdit={handleEditEmployee}
          onDeactivate={handleDeactivateEmployee}
          onDelete={handleDeleteEmployee}
          onViewHistory={handleViewHistory}
          isLoading={employeesLoading}
        />
      </div>

      {/* Employee Form Modal */}
      {showForm && (
        <EmployeeForm
          employee={selectedEmployee}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setShowForm(false);
            setSelectedEmployee(null);
          }}
          isSubmitting={
            createEmployeeMutation.isPending || updateEmployeeMutation.isPending
          }
        />
      )}

      {/* Maintenance History Modal */}
      {showHistory && historyEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 p-4">
          <div className="relative my-8 mx-auto w-full max-w-5xl bg-white rounded-2xl shadow-2xl animate-in fade-in zoom-in duration-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-500 rounded-t-2xl p-6 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold mb-1">
                    Histórico de Manutenção
                  </h3>
                  <p className="text-green-100 text-sm">
                    {historyEmployee.name} - {historyEmployee.employeeCode}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowHistory(false);
                    setHistoryEmployee(null);
                  }}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Date Filter */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6 bg-gray-50 p-4 rounded-xl border border-gray-200">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Inicial
                  </label>
                  <input
                    type="date"
                    value={dateFilter.startDate}
                    onChange={(e) =>
                      setDateFilter({ ...dateFilter, startDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Final
                  </label>
                  <input
                    type="date"
                    value={dateFilter.endDate}
                    onChange={(e) =>
                      setDateFilter({ ...dateFilter, endDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handleExportHistory}
                    disabled={!maintenanceHistory || maintenanceHistory.length === 0}
                    className="w-full sm:w-auto px-6 py-2 bg-gradient-to-r from-green-600 to-green-500 text-white font-medium rounded-lg hover:from-green-700 hover:to-green-600 transition-all shadow-lg hover:shadow-xl flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Exportar
                  </button>
                </div>
              </div>

              {/* History Content */}
              {historyLoading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-200 border-t-green-600"></div>
                </div>
              ) : maintenanceHistory && maintenanceHistory.length > 0 ? (
                <>
                  {/* Mobile Card View */}
                  <div className="block lg:hidden space-y-3 max-h-96 overflow-y-auto">
                    {maintenanceHistory.map((entry) => (
                      <div
                        key={entry.session.id}
                        className="bg-white rounded-lg shadow border border-gray-200 p-4"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-bold text-gray-900">
                              {entry.machine.code}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {entry.machine.location}
                            </p>
                          </div>
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              entry.session.status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {entry.session.status === 'completed'
                              ? 'Concluído'
                              : 'Em andamento'}
                          </span>
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Início:</span>
                            <span className="font-medium">
                              {formatDateTime(entry.session.startTime)}
                            </span>
                          </div>
                          {entry.session.endTime && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Fim:</span>
                              <span className="font-medium">
                                {formatDateTime(entry.session.endTime)}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-gray-600">Duração:</span>
                            <span className="font-medium">
                              {formatDuration(entry.session.duration)}
                            </span>
                          </div>
                          {entry.session.notes && (
                            <div className="pt-2 border-t border-gray-200">
                              <span className="text-gray-600 text-xs">Notas:</span>
                              <p className="text-gray-900 mt-1">
                                {entry.session.notes}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop Table View */}
                  <div className="hidden lg:block overflow-x-auto max-h-96">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Máquina
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Início
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Fim
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Duração
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Notas
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {maintenanceHistory.map((entry) => (
                          <tr key={entry.session.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {entry.machine.code}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {entry.machine.location}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDateTime(entry.session.startTime)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {entry.session.endTime
                                ? formatDateTime(entry.session.endTime)
                                : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDuration(entry.session.duration)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                                  entry.session.status === 'completed'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}
                              >
                                {entry.session.status === 'completed'
                                  ? 'Concluído'
                                  : 'Em andamento'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                              {entry.session.notes || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <svg
                    className="w-16 h-16 mx-auto text-gray-400 mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p className="text-gray-500">
                    Nenhum histórico de manutenção encontrado para o período
                    selecionado.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen && confirmModal.type === 'deactivate'}
        onClose={() => setConfirmModal({ isOpen: false, type: null, employee: null })}
        onConfirm={handleConfirmAction}
        title="Desativar Funcionário"
        message={`Tem certeza que deseja desativar o funcionário ${confirmModal.employee?.name}?\n\nIsso impedirá que ele acesse o sistema, mas manterá seu histórico de manutenção.`}
        confirmText="Desativar"
        cancelText="Cancelar"
        confirmButtonClass="bg-yellow-600 hover:bg-yellow-700"
        icon="warning"
      />

      <ConfirmationModal
        isOpen={confirmModal.isOpen && confirmModal.type === 'delete'}
        onClose={() => setConfirmModal({ isOpen: false, type: null, employee: null })}
        onConfirm={handleConfirmAction}
        title="⚠️ Excluir Permanentemente"
        message={
          <div className="text-left space-y-3">
            <p className="font-semibold text-center">
              Tem certeza que deseja EXCLUIR PERMANENTEMENTE o funcionário {confirmModal.employee?.name}?
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
              <p className="font-semibold text-red-800 mb-2">Esta ação:</p>
              <ul className="list-disc list-inside space-y-1 text-red-700">
                <li>NÃO PODE ser desfeita</li>
                <li>Removerá TODOS os dados do funcionário</li>
                <li>Removerá TODO o histórico de manutenção</li>
                <li>Removerá TODAS as agendas associadas</li>
              </ul>
            </div>
            <p className="text-sm text-gray-600 text-center">
              Se você deseja apenas impedir o acesso, use "Desativar" ao invés de "Excluir".
            </p>
          </div>
        }
        confirmText="Continuar"
        cancelText="Cancelar"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        icon="danger"
      />

      <ConfirmationModal
        isOpen={confirmModal.isOpen && confirmModal.type === 'deleteConfirm'}
        onClose={() => setConfirmModal({ isOpen: false, type: null, employee: null })}
        onConfirm={handleConfirmAction}
        title="🚨 ÚLTIMA CONFIRMAÇÃO"
        message={
          <div className="space-y-3">
            <p className="font-bold text-red-600 text-lg">
              Esta é sua última chance!
            </p>
            <p>
              Você está prestes a excluir permanentemente:
            </p>
            <div className="bg-gray-100 rounded-lg p-3">
              <p className="font-mono font-bold text-lg text-center">
                {confirmModal.employee?.name}
              </p>
              <p className="text-sm text-gray-600 text-center">
                {confirmModal.employee?.employeeCode}
              </p>
            </div>
            <p className="text-sm font-semibold text-red-600">
              Todos os dados serão perdidos para sempre!
            </p>
          </div>
        }
        confirmText="SIM, EXCLUIR PERMANENTEMENTE"
        cancelText="Não, Cancelar"
        confirmButtonClass="bg-red-700 hover:bg-red-800"
        icon="danger"
      />
    </div>
  );
};
